import { BtnClasses, Button, IconButton, WithLabel } from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';
import { For, Show, type Component, type JSX } from 'solid-js';

export interface PropertySetItemProperty {
  key: string;
  name: string;
  value: JSX.Element;
  /**
   * When set, the value is rendered as a link to this URL (opens in a new tab),
   * matching tcweb's clickable pset values.
   */
  href?: string;
  /**
   * Whether the property value text is muted (italic and dim gray).
   */
  muted?: boolean;
  isFavorite?: boolean;
}

export interface PropertySetItemProps {
  testId: string;
  name: string;
  expanded: boolean;
  properties: PropertySetItemProperty[];
  secondaryProperties?: PropertySetItemProperty[];
  onToggle(): void;
  onToggleFavorite?(propertyKey: string): void;
  secondaryPropertiesExpanded?: boolean;
  onSecondaryPropertiesToggle?(): void;
}

export const PropertySetItem: Component<PropertySetItemProps> = (props) => {
  const PropertyItem: Component<PropertySetItemProperty> = (prop) => (
    <div class="flex items-center justify-between pr-[0.25rem]">
      <WithLabel label={prop.name} name="">
        <Show
          when={prop.href !== undefined}
          fallback={
            <span
              class={`break-words ${prop.muted ? 'italic text-[#8c8b96]' : ''}`}
            >
              {prop.value}
            </span>
          }
        >
          <a
            href={prop.href}
            target="_blank"
            rel="noopener noreferrer"
            class="break-all text-[#0c77be] hover:underline"
          >
            {prop.value}
          </a>
        </Show>
      </WithLabel>
      <IconButton
        color={BtnClasses.TERTIARY}
        icon={prop.isFavorite ? 'tc-icon-star' : 'tc-icon-star-border'}
        classes={prop.isFavorite ? 'text-[#0c77be]' : ''}
        size="icon-small"
        data-cy="property-favorite-toggle"
        title={
          prop.isFavorite
            ? t('objectProperties.removeFavorite')
            : t('objectProperties.markAsFavorite')
        }
        clicked={(event) => {
          event.preventDefault();
          event.stopPropagation();
          props.onToggleFavorite?.(prop.key);
        }}
      />
    </div>
  );

  return (
    <div
      class="border-b border-[#e2e2e7]"
      data-testid={props.testId}
      aria-expanded={props.expanded}
    >
      <header
        role="button"
        class="flex pl-4 pr-2 cursor-pointer select-none hover:bg-[#f3f3f7]"
        onClick={() => props.onToggle()}
      >
        <h5 class="flex flex-1 items-center text-ellipsis">{props.name}</h5>
        <IconButton
          color={BtnClasses.TERTIARY}
          icon={props.expanded ? 'tc-icon-chevron-up' : 'tc-icon-chevron-down'}
          size="icon-medium"
          data-cy="properties-toggle-button"
          clicked={(event) => {
            event.preventDefault();
            event.stopPropagation();
            props.onToggle();
          }}
        />
      </header>

      <Show when={props.expanded}>
        <section class="pl-4 pr-2 pb-2">
          <For each={props.properties}>
            {(prop) => <PropertyItem {...prop} />}
          </For>
          <Show
            when={
              props.secondaryPropertiesExpanded !== undefined &&
              props.onSecondaryPropertiesToggle
            }
          >
            <Button
              color={BtnClasses.PRIMARY}
              type="hollow"
              size="medium"
              content={
                props.secondaryPropertiesExpanded
                  ? `- ${t('objectProperties.showLess')}`
                  : `+ ${t('objectProperties.showAll')}`
              }
              onClick={() => {
                props.onSecondaryPropertiesToggle?.();
              }}
            />
          </Show>
          <For each={props.secondaryProperties ?? []}>
            {(prop) => <PropertyItem {...prop} />}
          </For>
        </section>
      </Show>
    </div>
  );
};
